import React, { FC } from 'react';
import { Box, Flex, HStack, SimpleGrid } from '@chakra-ui/react';

import SunshineIconButton from 'components/_core/IconButton';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineDropdown from 'components/_core/SunshineDropdown';
import { IconName } from 'components/_core/SunshineIcon';

import { ItemPicture } from './components/ItemPicture';
import { StatIndicator, StatIndicatorMode } from './components/StatIndicator';

type ContextMenuItem = React.ComponentProps<typeof SunshineDropdown.Item> & {
  label: string;
  id: string;
};

export interface ContextMenu {
  items: ContextMenuItem[];
}

interface TransactionAggregationTableItemContextMenuProps {
  contextMenu?: ContextMenu;
  onContextMenuClicked: (contextMenuItemId: string) => void;
}

export type TransactionAggregationTableItem = {
  logo?: string;
  icon?: IconName;
  title: string;
  subtitle?: string | null;
  delta?: number | null;
  currentValue?: number | null;
  transactionCount?: number | null;
  mode?: StatIndicatorMode;
  contextMenu?: ContextMenu;
};

const TransactionAggregationTableItemContextMenu: FC<
  TransactionAggregationTableItemContextMenuProps
> = ({ contextMenu, onContextMenuClicked }) => {
  if (!contextMenu?.items?.length) {
    return null;
  }

  return (
    <SunshineDropdown>
      <SunshineDropdown.Trigger>
        <SunshineIconButton
          aria-label="Actions"
          icon="vertical-ellipsis"
          isDisabled={false}
          isLoading={false}
          variant="secondary"
        />
      </SunshineDropdown.Trigger>

      <SunshineDropdown.List>
        {contextMenu.items.map((item) => {
          return (
            <SunshineDropdown.Item
              key={item.id}
              {...item}
              onClick={() => onContextMenuClicked(item.id)}
            >
              {item.label}
            </SunshineDropdown.Item>
          );
        })}
      </SunshineDropdown.List>
    </SunshineDropdown>
  );
};

export const TransactionAggregationItem: FC<
  TransactionAggregationTableItem & {
    mode: StatIndicatorMode;
    colorSchema?: 'arrow-normal' | 'arrow-reversed';
  } & TransactionAggregationTableItemContextMenuProps
> = ({
  colorSchema,
  contextMenu,
  currentValue,
  delta,
  icon,
  logo,
  mode,
  onContextMenuClicked,
  subtitle,
  title,
  transactionCount,
}) => {
  return (
    <SunshineCard>
      <SimpleGrid alignItems="center" columns={5} width="100%">
        <HStack gridColumn="span 2" spacing="space-16">
          <Box>
            <ItemPicture icon={icon} logo={logo} title={title} />
          </Box>

          <SunshineCard.Content>
            <SunshineCard.Title noOfLines={1} wordBreak="break-all">
              {title}
            </SunshineCard.Title>

            {subtitle ? (
              <SunshineCard.Text noOfLines={1} wordBreak="break-all">
                {subtitle}
              </SunshineCard.Text>
            ) : null}
          </SunshineCard.Content>
        </HStack>

        <StatIndicator
          colorSchema={colorSchema}
          currentValue={currentValue}
          delta={delta}
          mode={mode}
          transactionCount={transactionCount}
        />

        <Flex alignItems="center" justifyContent="flex-end">
          <TransactionAggregationTableItemContextMenu
            contextMenu={contextMenu}
            onContextMenuClicked={onContextMenuClicked}
          />
        </Flex>
      </SimpleGrid>
    </SunshineCard>
  );
};
